import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import pickBy from 'lodash/pickBy';
import isNil from 'lodash/isNil';
import { useRouteMatch, useHistory } from 'react-router-dom';
import { useMount } from 'react-use';
import isEmpty from 'lodash/isEmpty';

import { Logger } from '../../../../../common/infrastructure/logger';
import {
  CustomError,
  useAppErrors,
} from '../../../../../common/contexts/app-error-context';
import Responsive, {
  useMobile,
} from '../../../../../common/components/responsive/Responsive';
import {
  ADD_FEATURE_TYPE,
  compilePath,
  FEEDBACK_WIZARD_STEP,
} from '../../../../../common/utils/helpers/routes';
import {
  formStateChanged,
  selectFormState,
  clearFormState,
  selectMarkerPositionCountryCode,
  clearRoutablePoints,
  selectCurrentFormId,
  currentFormIdChanged,
} from '../../../../../common/store/item-edit';
import { getLastSubmittedEmail } from '../../../../../common/utils/helpers/emailCache';
import {
  initialLoadingPhotosHandler,
  newPhotosHandler,
  photosReducer,
} from '../../../../../common/components/controls/photo-upload/PhotoUpload';
import {
  EDIT_SUGGESTION_TYPES,
  COUNTRIES_WITH_MILES_CODES,
  EDIT_SUGGESTION_VERSION,
} from '../../../../../common/utils/constants/constants';
import { SPEED_UNITS } from '../../../../../common/components/speed-limit/SpeedLimit';
import { selectUserNotificationEnabled } from '../../../../../common/store/user-notificaition-settings';
import Form, {
  FormField,
} from '../../../../../common/components/forms/form/Form';
import DefaultFormFooterHint from '../../../../../common/components/forms/default-form-footer-hint/DefaultFormFooterHint';
import { FIELD_TYPES } from '../../../../../common/components/forms/field/Field';
import { validators } from '../../../../../common/utils/helpers/validators';
import {
  sendEditSuggestionAction,
  storeLastSubmittedEmailAction,
  analyticsTrackAction,
} from '../../../../../common/utils/helpers/EditSuggestionActions';

import './AddSpeedLimitForm.scss';
import { getReferrer } from '../../../../../common/utils/helpers/editSuggestions';

const getAdditionalInformationFormFields = ({
  attachmentsDispatch,
}): FormField[] => [
  {
    field: 'email',
    label: 'Your email',
    defaultValue: '',
    getPrefillValue: () => getLastSubmittedEmail(),
    required: true,
    validate: validators.compose(
      validators.required('Email'),
      validators.email,
    ),
    type: FIELD_TYPES.input,
    controlProps: {
      placeholder: 'You will get an email when changes are made',
      // name and autocomplete are required for email autocompletion done by browser
      name: 'email',
      autoComplete: 'email',
    },
  },
  {
    field: 'comment',
    label: 'Comments',
    defaultValue: '',
    type: FIELD_TYPES.textArea,
    controlProps: {
      placeholder: 'Add here some additional information',
    },
  },
  {
    field: 'attachments',
    label: 'Photos',
    defaultValue: {},
    type: FIELD_TYPES.photos,
    controlProps: { photosDispatch: attachmentsDispatch },
  },
];

const feedbackFormFields: FormField[] = [
  {
    field: 'speedLimit',
    label: 'Speed limit',
    required: true,
    checkChanged: (value, initialValue) => value?.value !== initialValue?.value,
    getValueOnOuterInputChange: async ({
      outerInput: { markerPositionCountryCode } = {},
      getValue,
      getTouched,
    }: {
      outerInput: { markerPositionCountryCode? };
      getValue;
      getTouched;
    }) => {
      if (getTouched()) {
        return getValue();
      }

      const unit = COUNTRIES_WITH_MILES_CODES[markerPositionCountryCode]
        ? SPEED_UNITS.milesPerHour
        : SPEED_UNITS.kilometersPerHour;

      return {
        value: getValue().value,
        unit,
      };
    },
    validate: validators.validateSelected(
      validators.required('Speed limit'),
      ({ value }) => value,
    ),
    defaultValue: {},
    type: FIELD_TYPES.speedLimit,
  },
];

const getJointFormFields = ({ attachmentsDispatch }): FormField[] => [
  ...feedbackFormFields,
  ...getAdditionalInformationFormFields({ attachmentsDispatch }),
];

const FORM_ID = 'AddSpeedLimitForm';

export interface AddSpeedLimitForm {
  userLocation;
  itemDragPosition;
  formDisabled;
  onSubmit;
  onCancel;
}

interface RouteMatch {
  wizardStep;
}

const AddSpeedLimitForm = ({
  userLocation,
  itemDragPosition,
  formDisabled,
  onSubmit,
  onCancel,
}: AddSpeedLimitForm) => {
  const { showErrorPage } = useAppErrors();
  const history = useHistory();
  const match = useRouteMatch<RouteMatch>();
  const isMobile = useMobile();
  const [isLoading, setLoadingState] = useState(false);

  const notificationsEnabled = useSelector(selectUserNotificationEnabled);
  const formState = useSelector(selectFormState);
  const currentFormId = useSelector(selectCurrentFormId);
  const formStateRef = useRef<Record<string, unknown>>();
  formStateRef.current = formState;

  const dispatch = useDispatch();
  const setFormState = useCallback(
    (formState) => dispatch(formStateChanged(formState)),
    [dispatch],
  );

  // clean up other forms to keep local storage size as small as possible
  useMount(() => {
    if (currentFormId !== FORM_ID) {
      dispatch(clearFormState());
      dispatch(currentFormIdChanged(FORM_ID));
    }

    dispatch(clearRoutablePoints());
  });

  const [isFeedbackFormShown, setIsFeedbackFormShown] = useState(!!formState);
  // guarantee that feedback is always filled with all the data on mobile
  useEffect(() => {
    if (!isFeedbackFormShown) {
      if (
        isMobile &&
        match.params.wizardStep !== FEEDBACK_WIZARD_STEP.feedback
      ) {
        history.replace({
          pathname: compilePath('add', null, {
            featureType: ADD_FEATURE_TYPE.speedLimit,
            wizardStep: FEEDBACK_WIZARD_STEP.feedback,
          }),
          search: history.location.search,
        });
      } else {
        setIsFeedbackFormShown(true);
      }
    } else {
      if (isMobile && !match.params.wizardStep) {
        history.replace({
          pathname: compilePath('add', null, {
            featureType: ADD_FEATURE_TYPE.speedLimit,
            wizardStep: FEEDBACK_WIZARD_STEP.feedback,
          }),
          search: history.location.search,
        });
      }
    }
  }, [isFeedbackFormShown, isMobile, match, history]);

  const attachmentsDispatch = useCallback(
    (action) => {
      if (!formStateRef.current) {
        return;
      }

      const newAttachments = photosReducer(
        (formStateRef.current.values as Record<string, any>).attachments,
        action,
      );
      const newFormState = {
        ...formStateRef.current,
        values: {
          ...(formStateRef.current.values as Record<string, unknown>),
          attachments: newAttachments,
        },
      };
      // set ref to handle sequential actions between state updates and rerenders
      formStateRef.current = newFormState;

      setFormState(newFormState);

      return action;
    },
    [setFormState],
  );

  const attachments = useMemo(() => formState?.values.attachments, [formState]);

  // reload all still loading images, because it won't be handled anyway
  useMount(() => {
    if (attachments) {
      initialLoadingPhotosHandler(attachments, attachmentsDispatch);
    }
  });

  useEffect(() => {
    if (attachments) {
      newPhotosHandler(attachments, attachmentsDispatch);
    }
  }, [attachments, attachmentsDispatch]);

  const jointFormFields = useMemo(
    () => getJointFormFields({ attachmentsDispatch }),
    [attachmentsDispatch],
  );
  const additionalInformationFormFields = useMemo(
    () =>
      getAdditionalInformationFormFields({
        attachmentsDispatch,
      }),
    [attachmentsDispatch],
  );

  const initialValues = useMemo(
    () =>
      jointFormFields.reduce((acc, { field, defaultValue }) => {
        acc[field] = defaultValue;
        return acc;
      }, {}),
    [jointFormFields],
  );

  useEffect(() => {
    if (!formState) {
      const values = jointFormFields.reduce(
        (acc, { field, defaultValue, getPrefillValue }) => {
          acc[field] = (getPrefillValue && getPrefillValue()) || defaultValue;
          return acc;
        },
        {},
      );

      setFormState({ values });
    }
  }, [formState, setFormState, jointFormFields]);

  const handleSubmit = useCallback(
    async (values) => {
      const { email, comment, attachments, ...changes } = values;
      const payload = pickBy(
        {
          version: EDIT_SUGGESTION_VERSION,
          action: 'create',
          reason: 'missing_speed_limit',
          location: itemDragPosition && {
            longitude: itemDragPosition[0],
            latitude: itemDragPosition[1],
          },
          userEmail: email,
          userLocation: !isEmpty(userLocation)
            ? {
                longitude: userLocation.longitude,
                latitude: userLocation.latitude,
              }
            : null,
          referrer: getReferrer(),
          notificationsDisabled: !notificationsEnabled,
          comment,
          attachments: Object.values(attachments as any[])
            .filter(({ fileId }) => fileId)
            .map(({ fileId }) => fileId),
          changes: pickBy(
            {
              ...changes,
              speedLimit:
                changes.speedLimit && changes.speedLimit.value
                  ? changes.speedLimit
                  : null,
            },
            (val) => !isNil(val),
          ),
        },
        (val) => !isNil(val),
      );

      try {
        setLoadingState(true);

        const editSuggestionResult = await sendEditSuggestionAction(
          {
            editSuggestionType: EDIT_SUGGESTION_TYPES.speedLimit,
            origin: FORM_ID,
          },
          payload,
        );

        storeLastSubmittedEmailAction(
          {
            email,
            origin: FORM_ID,
          },
          editSuggestionResult,
        );

        analyticsTrackAction({
          analyticsTrackingMessage: 'Speed limit Addition Submit',
          origin: FORM_ID,
          analyticsTrackParams: {
            coordinates: itemDragPosition,
          },
        });

        if (onSubmit) {
          onSubmit(editSuggestionResult.result);
        }
      } catch (error) {
        const _error: CustomError = error as CustomError;

        Logger.error('Error while submitting edit suggestion', _error.message);

        showErrorPage({
          type: _error.type,
          message: _error.message,
        } as CustomError);
      } finally {
        setLoadingState(false);
      }
    },
    [
      itemDragPosition,
      onSubmit,
      showErrorPage,
      userLocation,
      notificationsEnabled,
    ],
  );

  const handleFeedbackFormSubmit = useCallback(
    () =>
      history.push({
        pathname: compilePath('add', null, {
          featureType: ADD_FEATURE_TYPE.speedLimit,
          wizardStep: FEEDBACK_WIZARD_STEP.additionalInformation,
        }),
        search: history.location.search,
      }),
    [history],
  );

  const { wizardStep } = match.params;

  const markerPositionCountryCode = useSelector(
    selectMarkerPositionCountryCode,
  );

  const outerInput = useMemo(
    () => ({ markerPositionCountryCode }),
    [markerPositionCountryCode],
  );

  return (
    <>
      <Responsive mobile={false}>
        <Form
          className="add-speed-limit-form"
          state={formState}
          fields={jointFormFields}
          initialValues={initialValues}
          outerInput={outerInput}
          formDisabled={formDisabled || isLoading}
          onStateChange={setFormState}
          onSubmit={handleSubmit}
          onCancel={onCancel}
          footerHint={<DefaultFormFooterHint />}
        />
      </Responsive>
      <Responsive mobile={true}>
        {wizardStep === FEEDBACK_WIZARD_STEP.feedback && (
          <Form
            className="add-speed-limit-form"
            state={formState}
            fields={feedbackFormFields}
            initialValues={initialValues}
            submitLabel="Next"
            outerInput={outerInput}
            formDisabled={formDisabled}
            stepLabel="1/2"
            onStateChange={setFormState}
            onSubmit={handleFeedbackFormSubmit}
          />
        )}

        {wizardStep === FEEDBACK_WIZARD_STEP.additionalInformation && (
          <Form
            state={formState}
            fields={additionalInformationFormFields}
            initialValues={initialValues}
            formDisabled={isLoading}
            stepLabel="2/2"
            onStateChange={setFormState}
            onSubmit={handleSubmit}
            footerHint={<DefaultFormFooterHint />}
          />
        )}
      </Responsive>
    </>
  );
};

export default AddSpeedLimitForm;
